import { sportsCategories, liveMatches, userStreams } from '../../../mocks/sportsData';
import { useEffect, useRef, useState, useMemo } from 'react';

interface SportsCategoriesProps {
  categories: any[];
  imageServer?: string;
  onCategorySelect: (category: any) => void;
  selectedCategory: any;
}

export default function SportsCategories({ categories, imageServer, onCategorySelect, selectedCategory }: SportsCategoriesProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // 计算每个分类的实际直播数量
  const categoryLiveCounts = useMemo(() => {
    const counts: { [key: string]: number } = {};
    
    // 计算正在进行的赛事直播数量
    liveMatches.forEach(match => {
      if (match.status === 'live') {
        counts[match.category] = (counts[match.category] || 0) + 1;
      }
    });
    
    // 计算正在进行的用户直播数量
    userStreams.forEach(stream => {
      if (stream.isLive) {
        counts[stream.category] = (counts[stream.category] || 0) + 1;
      }
    });
    
    return counts;
  }, []);

  // 计算总直播数量
  const totalLiveCount = useMemo(() => {
    return Object.values(categoryLiveCounts).reduce((sum, count) => sum + count, 0);
  }, [categoryLiveCounts]);

  // 计算全部分类数量
  const totalNumber = useMemo(() => {
    return categories.reduce((sum, cat) => sum + (cat.number || 0), 0);
  }, [categories]);

  // 检查滚动状态
  const checkScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // 组件加载时检查是否需要显示滚动提示
  useEffect(() => {
    const checkNeedScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        const needScroll = scrollWidth > clientWidth;
        setShowScrollHint(needScroll);
        checkScrollState();
      }
    };

    checkNeedScroll();
    window.addEventListener('resize', checkNeedScroll);
    
    return () => window.removeEventListener('resize', checkNeedScroll);
  }, []);

  // 监听滚动事件
  const handleScroll = () => {
    checkScrollState();
  };

  // 左右滚动按钮功能
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-4 bg-white border-b border-gray-100">
      <div className="container mx-auto px-4">
        <div className="relative">
          {/* 左侧滚动按钮 */}
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute -left-2 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-gray-600"></i>
            </button>
          )}

          {/* 右侧滚动按钮 */}
          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute -right-2 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-gray-600"></i>
            </button>
          )}

          {/* 滚动容器 - 添加左右内边距避免按钮遮挡 */}
          <div 
            ref={scrollContainerRef}
            className={`flex overflow-x-auto scrollbar-hide gap-6 md:gap-8 pb-2 scroll-smooth transition-all duration-200 ${
              canScrollLeft ? 'pl-10' : 'pl-0'
            } ${
              canScrollRight ? 'pr-10' : 'pr-0'
            }`}
            onScroll={handleScroll}
            style={{ 
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitScrollbar: { display: 'none' }
            }}
          >
            {/* 全部分类 */}
            <button
              onClick={() => onCategorySelect({ id: 0, name: '全部' })}
              className={`flex flex-col items-center min-w-0 flex-shrink-0 group cursor-pointer transition-all duration-200 ${
                selectedCategory?.name === '全部' ? 'transform scale-105' : ''
              }`}
            >
              <div 
                className={`w-12 h-12 rounded-full flex items-center justify-center mb-1 border-2 ${
                  selectedCategory === '全部' 
                    ? 'bg-green-500 text-white border-green-500' 
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-green-300'
                }`}
              >
                <i className="ri-list-check-3 text-lg"></i>
              </div>
              <span className={`text-xs font-medium transition-colors whitespace-nowrap ${
                selectedCategory?.name === '全部' ? 'text-green-600' : 'text-gray-700 group-hover:text-green-600'
              }`}>
                全部
              </span>
              <span className="text-xs text-gray-500 mt-0.5">{totalNumber}</span>
            </button>

            {categories.map(category => {
              const liveCount = categoryLiveCounts[category.name] || 0;
              return (
                <button
                  key={category.id}
                  onClick={() => onCategorySelect(category)}
                  className={`flex flex-col items-center min-w-0 flex-shrink-0 group cursor-pointer transition-all duration-200 ${
                    selectedCategory?.id === category.id ? 'transform scale-105' : ''
                  }`}
                >
                  <div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-1 border-2 ${
                      selectedCategory?.id === category.id 
                        ? 'bg-gray-900 text-white border-gray-900' 
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={imageServer ? imageServer + category.logo : category.logo}
                      alt={category.name}
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <span className={`text-xs font-medium transition-colors whitespace-nowrap ${
                    selectedCategory?.id === category.id ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
                  }`}>
                    {category.name}
                  </span>
                  <span className="text-xs text-gray-500 mt-0.5">{category.number}</span>
                </button>
              );
            })}
          </div>

          {/* 拖动提示 - 只在移动端显示且内容可滚动时显示 */}
          {showScrollHint && (
            <div className="md:hidden mt-1 flex items-center justify-center text-xs text-gray-500 animate-pulse">
              <i className="ri-drag-move-line mr-1"></i>
              <span>左右滑动查看更多分类</span>
              <i className="ri-arrow-left-right-line ml-1"></i>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
